/**
 * Responsive Utilities
 * 
 * Hooks and utilities for responsive design across mobile, tablet, and desktop.
 * See docs/design/*.md for breakpoint specifications.
 */

import { useState, useEffect, useCallback } from 'react';
import { Dimensions, Platform } from 'react-native';
import { BREAKPOINTS, NAVIGATION, CONTENT } from '../tokens/layout';

// =============================================================================
// TYPES
// =============================================================================

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

export interface ResponsiveState {
  // Dimensions
  width: number;
  height: number;
  
  // Device type
  deviceType: DeviceType;
  
  // Boolean flags (convenience)
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // Mobile sub-breakpoint (for hiding tab labels on tiny phones)
  isSmallMobile: boolean;
  
  // Orientation (primarily for tablet)
  orientation: Orientation;
  isPortrait: boolean;
  isLandscape: boolean;
  
  // Platform
  isWeb: boolean;
  
  // Layout helpers
  sidebarWidth: number;
  contentPadding: number;
  showBottomTabs: boolean;
  showSidebar: boolean;
  sidebarExpanded: boolean;
}

// =============================================================================
// BREAKPOINTS (Re-exported for convenience)
// =============================================================================

export const breakpoints = {
  smallMobile: 400,  // Very small phones (iPhone SE)
  mobile: BREAKPOINTS.mobile,   // 768
  tablet: BREAKPOINTS.tablet,   // 1024
  desktop: BREAKPOINTS.desktop, // 1024
} as const;

// =============================================================================
// RESPONSIVE HOOK
// =============================================================================

/**
 * Hook for responsive design across mobile, tablet, and desktop.
 * Returns current screen dimensions, device type, and layout helpers.
 */
export function useResponsive(): ResponsiveState {
  const [dimensions, setDimensions] = useState(() => getInitialDimensions());

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions({ width: window.width, height: window.height });
    });
    return () => subscription?.remove();
  }, []);

  return computeResponsiveState(dimensions);
}

/**
 * Get initial dimensions safely (handles SSR and edge cases)
 */
function getInitialDimensions(): { width: number; height: number } {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return {
      width: window.innerWidth || 1024,
      height: window.innerHeight || 768,
    };
  }
  try {
    const { width, height } = Dimensions.get('window');
    return { width, height };
  } catch {
    return { width: 1024, height: 768 };
  }
}

/**
 * Compute responsive state from dimensions
 */
function computeResponsiveState(dimensions: { width: number; height: number }): ResponsiveState {
  const { width, height } = dimensions;
  
  // Device type (authoritative breakpoints)
  const isMobile = width < BREAKPOINTS.mobile;      // < 768
  const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet; // 768-1024
  const isDesktop = width >= BREAKPOINTS.tablet;    // >= 1024
  
  // Small mobile (for hiding tab labels)
  const isSmallMobile = width < 400;
  
  // Orientation
  const isPortrait = height >= width;
  const isLandscape = width > height;
  const orientation: Orientation = isPortrait ? 'portrait' : 'landscape';
  
  // Device type string
  const deviceType: DeviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  
  // Layout helpers
  const sidebarWidth = getSidebarWidth(deviceType, isLandscape);
  const contentPadding = getContentPadding(deviceType);
  const showBottomTabs = isMobile;
  const showSidebar = isTablet || isDesktop;
  const sidebarExpanded = isDesktop || (isTablet && isLandscape);
  
  return {
    width,
    height,
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    orientation,
    isPortrait,
    isLandscape,
    isWeb: Platform.OS === 'web',
    sidebarWidth,
    contentPadding,
    showBottomTabs,
    showSidebar,
    sidebarExpanded,
  };
}

// =============================================================================
// LAYOUT HELPERS
// =============================================================================

function getSidebarWidth(deviceType: DeviceType, isLandscape: boolean): number {
  if (deviceType === 'mobile') return 0;
  if (deviceType === 'tablet') {
    return isLandscape ? NAVIGATION.tabletSidebarExpanded : NAVIGATION.tabletSidebarCollapsed;
  }
  return NAVIGATION.desktopSidebarWidth;
}

function getContentPadding(deviceType: DeviceType): number {
  switch (deviceType) {
    case 'mobile': return CONTENT.paddingMobile;
    case 'tablet': return CONTENT.paddingTablet;
    case 'desktop': return CONTENT.paddingDesktop;
  }
}

// =============================================================================
// VALUE GETTERS (Non-hook, for use outside components)
// =============================================================================

/**
 * Get responsive value based on current breakpoint.
 * Use inside components with useResponsive() when possible.
 */
export function getResponsiveValue<T>(
  mobile: T,
  tablet?: T,
  desktop?: T
): T {
  const width = getCurrentWidth();
  
  if (width >= BREAKPOINTS.tablet && desktop !== undefined) {
    return desktop;
  }
  if (width >= BREAKPOINTS.mobile && tablet !== undefined) {
    return tablet;
  }
  return mobile;
}

/**
 * Get current window width (for non-hook usage)
 */
function getCurrentWidth(): number {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.innerWidth || 1024;
  }
  try {
    return Dimensions.get('window').width;
  } catch {
    return 1024;
  }
}

// =============================================================================
// DEPRECATED (Keep for backwards compatibility)
// =============================================================================

/**
 * @deprecated Use getResponsiveValue instead
 */
export function getResponsiveSpacing(
  base: number,
  multiplier: { mobile?: number; tablet?: number; desktop?: number } = {}
): number {
  const width = getCurrentWidth();
  
  if (width >= BREAKPOINTS.tablet && multiplier.desktop) {
    return base * multiplier.desktop;
  }
  if (width >= BREAKPOINTS.mobile && multiplier.tablet) {
    return base * multiplier.tablet;
  }
  return base * (multiplier.mobile || 1);
}
